// ── RETREATS ─────────────────────────────────────────────────────────
// The fall trip's behavior contracts and waivers. One nav entry, one view
// ('a-retreats') with its own small state machine rather than several routed
// views, because everything below the list belongs to whichever retreat is
// open:
//
//   list    -- the Retreats page (or its first-time empty state)
//   new     -- create a retreat
//   detail  -- one retreat, with three tabs: Signing status, People, Details
//
// Signing status is a checklist on purpose: signed or not, what's missing in
// plain words, and one Remind button. Lori's real questions are "who hasn't
// signed?", "can I nudge them?" and "where's the signed copy?" -- nothing here
// answers any other question.
//
// Adding people happens in a side panel over the People tab. Only performers
// who are 14 by September 1 of the departure year are offered; the server
// enforces the same rule.
//
// While RETREAT_EMAILS is off (the default) every send is logged, not sent,
// and each unfinished row offers "Open page" and "See email" so the whole flow
// can be walked without reaching a family. Bare globals used: esc.
import { apiFetch } from '../core/api.js';
import { fmtD } from '../core/format.js';
import { openModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, VIEW_HASH, VIEW_RESTORE, updateHash } from '../core/routing.js';
import { openHelpSection } from './admin-help.js';

let rtMode = 'list';
let rtList = null;
let rtId = null;
let rtData = null;
let rtTab = 'status';
let rtShowSigned = false;
let rtDrawer = null;       // null | 'performers' | 'chaperone'
let rtEligible = null;
let rtPicked = new Set();
let rtFilter = '';

// ── helpers ──────────────────────────────────────────────────────────

const longDate = iso => new Date(iso + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

function daysLeft(iso) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = Math.round((new Date(iso + 'T00:00') - today) / 86400000);
  if (d > 1) return d + ' days left';
  if (d === 1) return '1 day left';
  if (d === 0) return 'due today';
  return 'past due';
}

function redraw(push) {
  const main = document.getElementById('main');
  if (!main) return;
  main.innerHTML = VIEWS['a-retreats']();
  wire();
  updateHash(!!push);
}

// The hash carries where Lori is -- #a-retreats/new, #a-retreats/3/people --
// so a refresh reopens the same retreat on the same tab.
const TABS = ['status', 'people', 'details'];
VIEW_HASH['a-retreats'] = () => rtMode === 'new' ? 'new' : (rtMode === 'detail' && rtId ? rtId + '/' + rtTab : '');
// Called on page load and on Back/Forward, so an empty path must mean the
// list, not "stay wherever the module happens to be".
VIEW_RESTORE['a-retreats'] = parts => {
  rtDrawer = null;
  if (parts[0] === 'new') {
    rtMode = 'new';
  } else if (/^\d+$/.test(parts[0] || '')) {
    if (Number(parts[0]) !== rtId) rtData = null;
    rtMode = 'detail';
    rtId = Number(parts[0]);
    rtTab = TABS.includes(parts[1]) ? parts[1] : 'status';
  } else {
    rtMode = 'list';
    rtId = null;
    rtData = null;
    rtTab = 'status';
  }
};

// `push` marks a move Lori made (a Back/Forward step); without it the hash
// is only rewritten in place -- a re-render after loading or saving, or a
// view being shown again by Back itself.
async function loadList(push) {
  try { rtList = await apiFetch('/retreats'); } catch (e) { rtList = []; toastError(e.message); }
  redraw(push);
}

async function openRetreat(id, tab, push) {
  if (id !== rtId) { rtChecks = {}; rtFormsEditing = false; }
  rtMode = 'detail';
  rtId = id;
  if (tab) rtTab = tab;
  try { rtData = await apiFetch('/retreats/' + id); } catch (e) { toastError(e.message); rtMode = 'list'; }
  redraw(push);
}

async function reload() {
  if (rtId) rtData = await apiFetch('/retreats/' + rtId);
  redraw();
}

// ── pages ────────────────────────────────────────────────────────────

VIEWS['a-retreats'] = () => {
  if (rtMode === 'new') return newPage();
  if (rtMode === 'detail') return rtData ? detailPage() : '<div class="page-head"><h2 class="serif">Retreats</h2></div><div class="card"><p class="empty">Loading…</p></div>';
  return listPage();
};

VIEW_INIT['a-retreats'] = () => {
  if (rtMode === 'detail' && rtId) { openRetreat(rtId); return; }
  rtMode = rtMode === 'new' ? 'new' : 'list';
  if (rtMode === 'list') loadList(); else wire();
};

function listPage() {
  if (rtList === null) {
    return `<div class="page-head"><h2 class="serif">Retreats</h2></div><div class="card"><p class="empty">Loading…</p></div>`;
  }
  if (!rtList.length) {
    return `<div class="page-head"><h2 class="serif">Retreats</h2></div>
      <div class="card" style="text-align:center;padding:56px 40px">
        <div class="serif" style="font-size:22px;margin-bottom:10px">No retreats yet</div>
        <p style="color:var(--muted);max-width:520px;margin:0 auto 20px">Each year's trip is its own retreat: its dates, its forms, and who's going. Signed forms from past years stay available after you archive a retreat.</p>
        <div style="display:inline-flex;flex-direction:column;gap:8px;text-align:left;font-size:14px;margin-bottom:24px;padding:18px 22px;border-radius:10px;background:rgba(255,255,255,0.03)">
          <div><span style="color:var(--gold);font-weight:600">1</span>&nbsp; In Firma, upload this year's performer and chaperone PDFs as templates.</div>
          <div><span style="color:var(--gold);font-weight:600">2</span>&nbsp; Create the retreat here and pick those two templates.</div>
          <div><span style="color:var(--gold);font-weight:600">3</span>&nbsp; Add who's going, then email everyone their signing link.</div>
        </div>
        <div><button class="btn" style="width:auto;display:inline-block;padding:11px 22px" data-action="rt-new">Create a retreat</button></div>
      </div>`;
  }
  const open = rtList.filter(r => !r.archived);
  const archived = rtList.filter(r => r.archived);
  const openCards = open.map(r => {
    const total = r.performers + r.chaperones;
    const pct = total ? Math.round(100 * r.complete / total) : 0;
    const dates = r.returnsOn ? fmtD(r.departsOn) + ' – ' + fmtD(r.returnsOn) : 'Leaves ' + fmtD(r.departsOn);
    return `<div class="card" style="border-color:rgba(201,168,76,0.3)">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:14px">
        <div>
          <div class="serif" style="font-size:21px">${esc(r.name)}</div>
          <div class="rt-sub" style="font-size:14px;margin-top:4px">${dates} · ${r.performers} performers, ${r.chaperones} chaperones · forms due ${longDate(r.formsDueOn)}</div>
        </div>
        <button class="btn-ghost" data-action="rt-open" data-id="${r.id}">Open</button>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:8px">
        <div><span class="serif" style="font-size:24px">${r.complete} of ${total}</span> <span style="color:var(--muted)">signed</span></div>
        <div style="font-size:14px;color:var(--muted)">${daysLeft(r.formsDueOn)}</div>
      </div>
      <div class="rt-progress"><div style="width:${pct}%"></div></div>
    </div>`;
  }).join('');
  const archivedRows = archived.map(r => `<div class="card" style="display:flex;justify-content:space-between;align-items:center;padding:18px 24px">
      <div><div class="serif" style="font-size:17px;color:var(--muted)">${esc(r.name)}</div>
      <div class="rt-sub">Archived · ${r.complete} signed</div></div>
      <button class="btn-ghost" style="color:var(--muted);border-color:var(--border-soft)" data-action="rt-open" data-id="${r.id}">View</button>
    </div>`).join('');

  return `<div class="page-head" style="display:flex;justify-content:space-between;align-items:flex-end;gap:16px">
      <h2 class="serif">Retreats</h2>
      ${open.length ? '' : '<button class="btn" style="width:auto;padding:11px 20px" data-action="rt-new">New retreat</button>'}
    </div>
    ${openCards}
    ${archived.length ? `<div class="rt-sub" style="text-transform:uppercase;letter-spacing:.15em;font-size:11px;margin:26px 0 10px">Archived</div>${archivedRows}` : ''}
    <p class="rt-sub" style="font-size:13px;margin-top:18px">One retreat collects signatures at a time. Archive it after the trip, and next year starts from “New retreat”.</p>`;
}

function detailsFields(r) {
  r = r || {};
  return `
    <div class="field"><label for="rt-name">Retreat name <span class="plain-cap">(families see this in every email and on the signing page)</span></label>
      <input id="rt-name" value="${esc(r.name || '')}" maxlength="120" placeholder="New York City 2026"></div>
    <div class="field-row">
      <div class="field"><label for="rt-departs">Leaves</label><input id="rt-departs" type="date" value="${esc(r.departsOn || '')}"></div>
      <div class="field"><label for="rt-returns">Returns <span class="plain-cap">(optional)</span></label><input id="rt-returns" type="date" value="${esc(r.returnsOn || '')}"></div>
    </div>
    <div class="field"><label for="rt-due">Forms due</label><input id="rt-due" type="date" value="${esc(r.formsDueOn || '')}"></div>
    <div class="field" style="margin-bottom:0"><label for="rt-reply">Family replies go to <span class="plain-cap">(optional)</span></label>
      <input id="rt-reply" type="email" value="${esc(r.replyTo || '')}" placeholder="Leave blank to use the portal's usual reply-to"></div>`;
}

// ── The forms: two Firma templates ─────────────────────────────────
// The packets are templates built in Firma's editor; this only picks one per
// packet and checks it has the signers and name fields the portal fills in.
// Rendered into #rt-forms and refreshed in place, so loading the template
// list or a check never wipes what's been typed in the trip fields.
let rtTemplates = null;     // null | { available, templates: [{id, name}] }
let rtChecks = {};          // kind -> { state: 'checking'|'ok'|'bad'|'error', problems }
// Once both saved templates have passed the check, the card shows them as
// saved and asks Firma nothing; "Change templates" flips this and loads the
// pickers. Sending still checks live in Firma, so this is display only.
let rtFormsEditing = false;
const PACKETS = [
  ['performer', 'rt-tpl-p', 'Performer packet', 'Behavior contract signed by the performer, then the waiver signed by a parent'],
  ['chaperone', 'rt-tpl-c', 'Chaperone packet', 'Chaperone contract and waiver, signed by the chaperone'],
];

// `footer` is the card's own Save button (Details tab); the New retreat page
// has a single Create button instead.
function formsCard(signing, footer = '') {
  return `<div class="rt-card"><div class="rt-head">The forms</div><div id="rt-forms" data-signing="${signing}" style="padding:20px">${formsBody(signing)}</div>${footer}</div>`;
}

// A card's Save button, bottom-left under the fields it saves. Both cards
// save the whole form -- the server takes the retreat's details as one --
// so an edit left in the other card is never silently dropped.
const cardSave = label => `<div style="display:flex;padding:0 20px 20px"><button class="btn" style="width:auto;padding:9px 20px" data-action="rt-save">${label}</button></div>`;

function showSavedForms() {
  const r = (rtMode === 'detail' && rtData) ? rtData.retreat : null;
  return !!r && !rtFormsEditing && !!r.performerTemplateCheckedAt && !!r.chaperoneTemplateCheckedAt;
}

function savedFormsBody(r) {
  const rows = PACKETS.map(([kind, , label, sub]) => {
    const checked = new Date(r[kind + 'TemplateCheckedAt']).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `<div class="field">
      <label>${label}</label>
      <div class="rt-sub" style="margin:-2px 0 6px">${sub}</div>
      <div class="rt-match" style="font-size:13px"><div style="color:var(--cream);font-size:14px;margin-bottom:2px">${esc(r[kind + 'TemplateName'] || 'Chosen template')}</div>
        <span style="color:var(--green);font-weight:500">Ready to use</span> <span class="rt-sub">· checked ${checked}</span></div>
    </div>`;
  }).join('');
  return `${rows}
    <div style="display:flex;gap:18px;flex-wrap:wrap">
      ${r.archived ? '' : '<button class="link-btn" style="font-size:13px" data-action="rt-tpl-change">Change templates</button>'}
      <button class="link-btn" style="font-size:13px" data-action="rt-help-templates">How to build these templates in Firma</button>
    </div>`;
}

function formsBody(signing) {
  const r = (rtMode === 'detail' && rtData) ? rtData.retreat : {};
  if (showSavedForms()) return savedFormsBody(r);
  if (rtTemplates === null) return '<p class="empty">Loading Firma templates…</p>';
  if (rtTemplates.error) {
    return `<p style="font-size:14px;color:var(--gold-light);margin-bottom:6px">Couldn’t load the templates from Firma.</p>
      <p class="rt-sub" style="margin-bottom:12px">${esc(rtTemplates.error)}</p>
      <button class="btn-ghost btn-sm" data-action="rt-tpl-retry">Try again</button>`;
  }
  if (!rtTemplates.available) {
    return '<p class="rt-sub" style="font-size:14px">Firma isn’t connected on this server, so templates can’t be chosen here yet. Signing is simulated until it is.</p>';
  }
  const selected = { performer: r.performerTemplateId || '', chaperone: r.chaperoneTemplateId || '' };
  const pickers = PACKETS.map(([kind, id, label, sub]) => `
    <div class="field">
      <label for="${id}">${label}</label>
      <div class="rt-sub" style="margin:-2px 0 6px">${sub}</div>
      <select id="${id}" data-kind="${kind}">
        <option value="">Choose a Firma template…</option>
        ${rtTemplates.templates.map(t => `<option value="${esc(t.id)}" ${t.id === (document.getElementById(id)?.value ?? selected[kind]) ? 'selected' : ''}>${esc(t.name)}</option>`).join('')}
      </select>
      <div id="rt-chk-${kind}" style="margin-top:8px">${checkBox(kind)}</div>
    </div>`).join('');
  return `${signing === 'fake' ? '<p class="rt-sub" style="font-size:13.5px;margin-bottom:14px">Signing is simulated right now (preview mode). Choose and check the templates anyway, so they’re ready when it’s switched on.</p>' : ''}
    ${pickers}
    <button class="link-btn" style="font-size:13px" data-action="rt-help-templates">How to build these templates in Firma</button>`;
}

function checkBox(kind) {
  const c = rtChecks[kind];
  if (!c) return '';
  if (c.state === 'checking') return '<span class="rt-sub">Checking…</span>';
  if (c.state === 'ok') return '<div class="rt-match" style="font-size:13px"><span style="color:var(--green);font-weight:500">Ready to use</span></div>';
  const items = (c.problems || []).map(p => `<div>${esc(p)}</div>`).join('');
  return `<div style="padding:10px 12px;border-radius:8px;background:rgba(201,168,76,0.06);border:1px solid rgba(201,168,76,0.35);font-size:13px">
      <div style="color:var(--gold-light);font-weight:500;margin-bottom:4px">${c.state === 'error' ? 'Couldn’t check it' : 'Fix this in Firma, then check again'}</div>${items}
      <button class="link-btn" style="font-size:13px;margin-top:4px" data-recheck="${kind}">Check again</button></div>`;
}

function refreshForms() {
  const box = document.getElementById('rt-forms');
  if (!box) return;
  box.innerHTML = formsBody(box.dataset.signing);
  wireForms();
}

function wireForms() {
  const box = document.getElementById('rt-forms');
  if (!box) return;
  box.querySelectorAll('select[data-kind]').forEach(sel => sel.addEventListener('change', () => runCheck(sel.dataset.kind, sel.value)));
  box.querySelectorAll('[data-recheck]').forEach(b => b.addEventListener('click', () => {
    const sel = box.querySelector(`select[data-kind="${b.dataset.recheck}"]`);
    runCheck(b.dataset.recheck, sel ? sel.value : '');
  }));
  const retry = box.querySelector('[data-action="rt-tpl-retry"]');
  if (retry) retry.addEventListener('click', () => { rtTemplates = null; refreshForms(); loadTemplates(); });
  const change = box.querySelector('[data-action="rt-tpl-change"]');
  if (change) change.addEventListener('click', () => { rtFormsEditing = true; loadTemplates(); });
  const help = box.querySelector('[data-action="rt-help-templates"]');
  if (help) help.addEventListener('click', () => openHelpSection('retreat', 'templates'));
}

async function loadTemplates() {
  if (showSavedForms()) { wireForms(); return; }
  if (rtTemplates !== null) { refreshForms(); checkChosen(); return; }
  // A failed load says so, with the reason and a retry -- never "Firma isn't
  // connected", which is only true when the server has no Firma key.
  try { rtTemplates = await apiFetch('/retreats/firma-templates'); }
  catch (e) { rtTemplates = { error: e.message }; }
  refreshForms();
  checkChosen();
}

// Check whatever is already chosen, so the status shows on arrival.
function checkChosen() {
  for (const [kind, id] of PACKETS) {
    const sel = document.getElementById(id);
    if (sel && sel.value && !rtChecks[kind]) runCheck(kind, sel.value);
  }
}

async function runCheck(kind, templateId) {
  const slot = () => document.getElementById('rt-chk-' + kind);
  if (!templateId) { delete rtChecks[kind]; if (slot()) slot().innerHTML = ''; return; }
  rtChecks[kind] = { state: 'checking' };
  if (slot()) slot().innerHTML = checkBox(kind);
  try {
    // On a saved retreat, a pass on its saved template is recorded server-side,
    // so next visit shows it without asking Firma.
    const retreat = rtMode === 'detail' ? '&retreat=' + rtId : '';
    const res = await apiFetch('/retreats/template-check?id=' + encodeURIComponent(templateId) + '&kind=' + kind + retreat);
    rtChecks[kind] = res.ok ? { state: 'ok' } : { state: 'bad', problems: res.problems };
  } catch (e) {
    rtChecks[kind] = { state: 'error', problems: [e.message] };
  }
  if (slot()) { slot().innerHTML = checkBox(kind); wireForms(); }
}

function readFields() {
  const v = id => (document.getElementById(id) || {}).value || '';
  // With the saved view showing (or the pickers not loaded yet) there's no
  // <select>, so keep what's saved rather than clearing it.
  const saved = (rtMode === 'detail' && rtData) ? rtData.retreat : {};
  const tpl = (id, kept) => document.getElementById(id) ? v(id) : (kept || '');
  return {
    name: v('rt-name').trim(), departsOn: v('rt-departs'), returnsOn: v('rt-returns'),
    formsDueOn: v('rt-due'), replyTo: v('rt-reply').trim(),
    performerTemplateId: tpl('rt-tpl-p', saved.performerTemplateId), chaperoneTemplateId: tpl('rt-tpl-c', saved.chaperoneTemplateId)
  };
}

function newPage() {
  return `<div class="page-head">
      <button class="link-btn" data-action="rt-back">← Retreats</button>
      <h2 class="serif" style="margin-top:8px">New retreat</h2>
    </div>
    <div class="rt-cols">
      <div class="rt-card"><div class="rt-head">The trip</div><div style="padding:20px">${detailsFields(null)}</div></div>
      ${formsCard('new')}
    </div>
    <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:22px">
      <button class="btn-ghost" data-action="rt-back">Cancel</button>
      <button class="btn" style="width:auto;padding:11px 22px" data-action="rt-create">Create retreat</button>
    </div>`;
}

function detailPage() {
  const r = rtData.retreat;
  const people = rtData.people;
  const tabs = [['status', 'Signing status'], ['people', `People (${people.length})`], ['details', 'Details &amp; forms']]
    .map(([id, label]) => `<button class="${rtTab === id ? 'on' : ''}" data-action="rt-tab" data-tab="${id}">${label}</button>`).join('');
  const body = rtTab === 'people' ? peopleTab() : rtTab === 'details' ? detailsTab() : statusTab();
  return `<div class="page-head" style="margin-bottom:22px">
      <button class="link-btn" data-action="rt-back">Retreats</button> <span style="color:var(--muted)">/</span>
      <h2 class="serif" style="margin-top:6px">${esc(r.name)}${r.archived ? ' <span class="pill pill-off" style="font-size:12px;vertical-align:middle;font-family:inherit">Archived</span>' : ''}</h2>
    </div>
    ${previewNote()}
    <div class="rt-tabs">${tabs}</div>
    ${body}
    ${rtDrawer ? drawer() : ''}`;
}

function previewNote() {
  const p = rtData.preview;
  if (!p.emails && p.signing !== 'fake') return '';
  const bits = [];
  if (p.emails) bits.push('emails are logged instead of sent');
  if (p.signing === 'fake') bits.push('signing is simulated');
  return `<div class="rt-preview"><strong style="font-weight:600">Preview mode:</strong> ${bits.join(', and ')}. Use “Open page” to walk through a family's side.</div>`;
}

function statusTab() {
  const r = rtData.retreat;
  const people = rtData.people;
  const done = people.filter(p => p.status === 'complete');
  const todo = people.filter(p => p.status !== 'complete');
  const pct = people.length ? Math.round(100 * done.length / people.length) : 0;
  const preview = rtData.preview.emails;
  const archived = r.archived;

  if (!people.length) {
    return `<div class="card"><p class="empty">Nobody is on this retreat yet. <button class="link-btn" data-action="rt-tab" data-tab="people">Add people</button> to get started.</p></div>`;
  }

  // Counted in people, not emails. Siblings share one email, so a count of
  // links next to a count of people read as two numbers that didn't agree.
  const unsentCount = rtData.unsent.reduce((n, u) => n + u.names.length, 0);
  const banner = unsentCount && !archived ? `<div class="rt-banner">
      <span style="font-weight:500">${unsentCount === 1 ? '1 person hasn’t' : unsentCount + ' people haven’t'} been sent their link yet.</span>
      <button class="btn" style="width:auto;padding:9px 18px" data-action="rt-send">${unsentCount === 1 ? 'Send their link' : 'Send links'}</button>
    </div>` : '';

  // Remind is only for someone whose link has already gone out; before that,
  // the banner's Send is the only way their first email leaves.
  const remindable = todo.filter(p => p.linkSent);

  const extras = p => preview ? `<a class="rt-link" href="${esc(p.linkUrl)}" target="_blank" rel="noopener">Open page</a><button class="rt-link" data-action="rt-email" data-link="${p.linkId}">See email</button>` : '';
  const todoRows = todo.map(p => `<div class="rt-row">
      <div class="who">${esc(p.name)}</div>
      <div class="role">${p.kind === 'performer' ? 'Performer' : 'Chaperone'}</div>
      <div class="need ${p.status === 'performer_signed' ? 'waiting' : ''}">${p.linkSent ? esc(p.statusLabel) : 'Link not sent yet'}</div>
      ${extras(p)}
      <div class="act">${archived || !p.linkSent ? '' : `<button class="btn-ghost btn-sm" data-action="rt-remind" data-id="${p.id}">Remind</button>`}</div>
    </div>`).join('');

  const doneRows = done.map(p => `<div class="rt-row">
      <div class="who">${esc(p.name)}</div>
      <div class="role">${p.kind === 'performer' ? 'Performer' : 'Chaperone'}</div>
      <div class="need" style="width:auto;flex:1">Signed${p.signerName && p.kind === 'performer' ? ' · waiver by ' + esc(p.signerName) : ''}${p.completedAt ? ' · ' + fmtD(p.completedAt.slice(0, 10)) : ''}</div>
      ${p.hasPdf ? `<button class="btn-ghost btn-sm" data-action="rt-pdf" data-id="${p.id}">PDF</button>` : ''}
    </div>`).join('');

  return `
    <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px;flex-wrap:wrap;gap:8px">
      <div><span class="serif" style="font-size:30px">${done.length} of ${people.length}</span> <span style="font-size:16px;color:var(--muted)">signed</span></div>
      <div style="font-size:14px;color:var(--muted)">Due <span style="color:var(--cream);font-weight:500">${longDate(r.formsDueOn)}</span> · ${daysLeft(r.formsDueOn)}</div>
    </div>
    <div class="rt-progress" style="margin-bottom:22px"><div style="width:${pct}%"></div></div>
    ${banner}
    ${todo.length ? `<div class="rt-card">
      <div class="rt-head"><span>Still need to sign <span class="count">(${todo.length})</span></span>
        ${archived || !remindable.length ? '' : `<button class="btn-ghost btn-sm" data-action="rt-remind-all">Remind all ${remindable.length}</button>`}</div>
      ${todoRows}
    </div>` : `<div class="card"><p style="color:var(--green)">Everyone has signed.</p></div>`}
    <div class="rt-card">
      <div class="rt-head" style="${rtShowSigned && done.length ? '' : 'border-bottom:none'}"><span>Signed <span class="count">(${done.length})</span></span>
        ${done.length ? `<button class="link-btn" style="font-family:'Source Sans 3',sans-serif;font-size:13px" data-action="rt-toggle-signed">${rtShowSigned ? 'Hide' : 'Show all'}</button>` : ''}</div>
      ${rtShowSigned ? doneRows : ''}
    </div>`;
}

function peopleTab() {
  const people = rtData.people;
  const archived = rtData.retreat.archived;
  // Already in first-name order from the server.
  const perf = people.filter(p => p.kind === 'performer');
  const chap = people.filter(p => p.kind === 'chaperone');
  const remove = p => archived ? '' : `<button class="rt-link" data-action="rt-remove" data-id="${p.id}" aria-label="Remove ${esc(p.name)}">Remove</button>`;
  const perfRows = perf.map(p => `<div class="rt-row"><div class="who">${esc(p.name)}</div><div class="rt-sub" style="width:150px">${esc(p.family)}</div>${remove(p)}</div>`).join('');
  const chapRows = chap.map(p => `<div class="rt-row"><div class="who">${esc(p.name)}<div class="sub">${esc(p.email)}</div></div>
      <div class="rt-sub" style="width:170px">${p.parentOf ? 'Parent of ' + esc(p.parentOf) : 'Not a parent'}</div>${remove(p)}</div>`).join('');
  return `
    <p class="rt-sub" style="font-size:13.5px;margin-bottom:18px">New people show up on Signing status with a “Send their link” button, so you can check the list before anyone is emailed. Removing someone who hasn't signed cancels their forms. Anyone who already signed keeps their signed copy.</p>
    <div class="rt-cols">
      <div class="rt-card">
        <div class="rt-head"><span>Performers <span class="count">(${perf.length})</span></span>${archived ? '' : '<button class="btn-ghost btn-sm" data-action="rt-drawer" data-kind="performers">Add performers</button>'}</div>
        ${perfRows || '<div class="rt-row"><span class="rt-sub">Nobody yet.</span></div>'}
      </div>
      <div class="rt-card">
        <div class="rt-head"><span>Chaperones <span class="count">(${chap.length})</span></span>${archived ? '' : '<button class="btn-ghost btn-sm" data-action="rt-drawer" data-kind="chaperone">Add a chaperone</button>'}</div>
        ${chapRows || '<div class="rt-row"><span class="rt-sub">Nobody yet.</span></div>'}
      </div>
    </div>`;
}

function detailsTab() {
  const r = rtData.retreat;
  return `
    <div class="rt-cols">
      <div class="rt-card"><div class="rt-head">The trip</div><div style="padding:20px">${detailsFields(r)}</div>${r.archived ? '' : cardSave('Save')}</div>
      ${formsCard(rtData.preview.signing, r.archived ? '' : cardSave('Save'))}
    </div>
    ${r.archived ? '' : `<div class="card" style="display:flex;justify-content:space-between;align-items:center;gap:24px;margin-top:28px">
      <div><div style="font-size:15px;font-weight:500">Archive this retreat</div>
        <div class="rt-sub" style="font-size:13px">After the trip. Unsigned links stop working and reminders stop; the signed records stay.</div></div>
      <button class="btn-ghost" style="color:var(--muted);border-color:var(--border-soft)" data-action="rt-archive">Archive retreat</button>
    </div>`}`;
}

// ── side panels ──────────────────────────────────────────────────────

function drawer() {
  const inner = rtDrawer === 'performers' ? performerDrawer() : chaperoneDrawer();
  return `<div class="rt-scrim" data-action="rt-close"></div><div class="rt-drawer" role="dialog" aria-label="${rtDrawer === 'performers' ? 'Add performers' : 'Add a chaperone'}">${inner}</div>`;
}

function performerDrawer() {
  const closeBtn = '<button class="modal-close" style="position:static" data-action="rt-close" aria-label="Close"><svg viewBox="0 0 16 16"><path d="M4 4l8 8M12 4l-8 8"/></svg></button>';
  if (!rtEligible) {
    return `<div class="dh">Add performers ${closeBtn}</div><div class="db"><p class="empty">Loading…</p></div>`;
  }
  const f = rtFilter.toLowerCase();
  const list = rtEligible.performers.filter(p => !f || p.name.toLowerCase().includes(f));
  const cutoff = new Date(rtEligible.ageCutoff + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const rows = list.map(p => `<label class="rt-pick ${p.going ? 'going' : ''}">
      <input type="checkbox" data-pick="${p.id}" ${p.going || rtPicked.has(p.id) ? 'checked' : ''} ${p.going ? 'disabled' : ''}>
      <span style="flex:1">${esc(p.name)} <span class="rt-sub">· ${esc(p.family)} · ${p.age}</span></span>
      ${p.going ? '<span class="rt-sub">already going</span>' : ''}
    </label>`).join('');
  return `<div class="dh">Add performers ${closeBtn}</div>
    <div class="db">
      <div style="font-size:14px;color:var(--muted)">Only performers who are <span style="color:var(--cream);font-weight:500">14 or older on ${cutoff}</span> are listed.</div>
      <input id="rt-filter" aria-label="Filter by name" placeholder="Filter by name" value="${esc(rtFilter)}">
      <div class="rt-pick-list">${rows || '<p class="empty" style="padding:14px">Nobody matches.</p>'}</div>
    </div>
    <div class="df">
      <span class="rt-sub" style="font-size:14px" id="rt-pick-count">${pickCountLabel()}</span>
      <div style="display:flex;gap:10px">
        <button class="btn-ghost" data-action="rt-close">Cancel</button>
        <button class="btn" style="width:auto;padding:9px 18px" data-action="rt-add-perf" id="rt-pick-add" ${rtPicked.size ? '' : 'disabled'}>${pickAddLabel()}</button>
      </div>
    </div>`;
}

const pickCountLabel = () => rtPicked.size + ' selected';
const pickAddLabel = () => {
  const n = rtPicked.size;
  return n === 0 ? 'Add performers' : n === 1 ? 'Add 1 performer' : 'Add ' + n + ' performers';
};

function chaperoneDrawer() {
  const closeBtn = '<button class="modal-close" style="position:static" data-action="rt-close" aria-label="Close"><svg viewBox="0 0 16 16"><path d="M4 4l8 8M12 4l-8 8"/></svg></button>';
  const families = rtData.families.map(f => `<option value="${f.accountId}">${esc(f.label)}</option>`).join('');
  return `<div class="dh">Add a chaperone ${closeBtn}</div>
    <div class="db">
      <div class="field-row">
        <div class="field"><label for="rt-c-first">First name</label><input id="rt-c-first"></div>
        <div class="field"><label for="rt-c-last">Last name</label><input id="rt-c-last"></div>
      </div>
      <div class="field" style="margin-bottom:0"><label for="rt-c-email">Email <span class="plain-cap">(their link goes here)</span></label><input id="rt-c-email" type="email"></div>
      <div id="rt-c-match" class="rt-match" style="display:none"></div>
      <div class="field"><label for="rt-c-parent">Parent of</label>
        <select id="rt-c-parent"><option value="">Not a parent</option>${families}</select>
        <div class="rt-sub" style="margin-top:6px">Only kids already going are listed. Choose “Not a parent” for staff, alumni, and family friends.</div>
      </div>
    </div>
    <div class="df">
      <button class="btn-ghost" data-action="rt-close">Cancel</button>
      <div style="display:flex;gap:10px">
        <button class="btn-ghost" data-action="rt-add-chap" data-again="1">Add and start another</button>
        <button class="btn" style="width:auto;padding:9px 18px" data-action="rt-add-chap">Add chaperone</button>
      </div>
    </div>`;
}

// When the typed email is a family's portal email, that person is that
// family's parent: say so and pick the family, which is what lets one email
// cover their own forms and their kids'.
function matchChaperoneEmail() {
  const email = (document.getElementById('rt-c-email').value || '').trim().toLowerCase();
  const box = document.getElementById('rt-c-match');
  const fam = rtData.families.find(f => f.email.toLowerCase() === email);
  if (!fam) { box.style.display = 'none'; return; }
  document.getElementById('rt-c-parent').value = String(fam.accountId);
  box.style.display = 'block';
  box.innerHTML = `<div style="color:var(--green);font-weight:500">This is the ${esc(fam.label.split(' ').pop())} family's portal email</div>
    <div>Set as parent of ${esc(fam.label)}. One email will cover their own forms and their kids'.</div>`;
}

// ── actions ──────────────────────────────────────────────────────────

function wire() {
  const main = document.getElementById('main');
  if (!main) return;
  const on = (action, fn) => main.querySelectorAll(`[data-action="${action}"]`).forEach(el => el.addEventListener('click', () => fn(el)));

  on('rt-new', () => { rtMode = 'new'; redraw(true); });
  on('rt-back', () => { rtMode = 'list'; rtData = null; rtId = null; rtTab = 'status'; loadList(true); });
  on('rt-open', el => openRetreat(Number(el.dataset.id), 'status', true));
  on('rt-tab', el => { rtTab = el.dataset.tab; rtDrawer = null; redraw(true); });
  on('rt-toggle-signed', () => { rtShowSigned = !rtShowSigned; redraw(); });
  on('rt-create', create);
  on('rt-save', save);
  on('rt-archive', archive);
  on('rt-send', sendLinks);
  on('rt-remind', el => remind([Number(el.dataset.id)]));
  on('rt-remind-all', () => remind(null));
  on('rt-email', el => showEmail(Number(el.dataset.link)));
  on('rt-pdf', el => openPdf(Number(el.dataset.id)));
  on('rt-remove', el => removePerson(Number(el.dataset.id)));
  on('rt-drawer', el => openDrawer(el.dataset.kind));
  on('rt-close', () => { rtDrawer = null; redraw(); });
  on('rt-add-perf', addPerformers);
  on('rt-add-chap', el => addChaperone(el.dataset.again === '1'));

  main.querySelectorAll('[data-pick]').forEach(cb => cb.addEventListener('change', () => {
    const id = Number(cb.dataset.pick);
    if (cb.checked) rtPicked.add(id); else rtPicked.delete(id);
    // Update the footer in place. A full redraw would rebuild the list and
    // throw Lori back to the top of it after every tick.
    document.getElementById('rt-pick-count').textContent = pickCountLabel();
    const add = document.getElementById('rt-pick-add');
    add.textContent = pickAddLabel();
    add.disabled = rtPicked.size === 0;
  }));
  const filter = document.getElementById('rt-filter');
  if (filter) {
    filter.addEventListener('input', () => {
      rtFilter = filter.value;
      const pos = filter.selectionStart;
      redraw();
      const again = document.getElementById('rt-filter');
      again.focus();
      again.setSelectionRange(pos, pos);
    });
  }
  const email = document.getElementById('rt-c-email');
  if (email) email.addEventListener('input', matchChaperoneEmail);
  if (document.getElementById('rt-forms')) loadTemplates();
}

async function create() {
  try {
    const r = await apiFetch('/retreats', { method: 'POST', body: JSON.stringify(readFields()) });
    toast('Retreat created');
    rtShowSigned = false;
    openRetreat(r.id, 'people', true);
  } catch (e) { toastError(e.message); }
}

async function save() {
  try {
    await apiFetch('/retreats/' + rtId, { method: 'PUT', body: JSON.stringify(readFields()) });
    toast('Saved');
    rtFormsEditing = false;
    await reload();
  } catch (e) { toastError(e.message); }
}

async function archive() {
  if (!confirm(`Archive ${rtData.retreat.name}? Unsigned links will stop working and no more reminders can be sent.`)) return;
  try {
    await apiFetch('/retreats/' + rtId + '/archive', { method: 'POST', body: '{}' });
    toast('Retreat archived');
    await reload();
  } catch (e) { toastError(e.message); }
}

async function sendLinks() {
  // Report people, matching the banner. The server counts emails, and
  // siblings share one, so its number would read as a different answer.
  const people = rtData.unsent.reduce((n, u) => n + u.names.length, 0);
  const who = people === 1 ? '1 person' : people + ' people';
  try {
    const res = await apiFetch('/retreats/' + rtId + '/send-links', { method: 'POST', body: '{}' });
    toast(res.suppressed ? `Links for ${who} logged (preview mode, not sent)` : `Links sent to ${who}`);
    await reload();
  } catch (e) { toastError(e.message); }
}

async function remind(ids) {
  if (ids === null && !confirm('Send a reminder to everyone whose link has gone out and who still needs to sign?')) return;
  try {
    const res = await apiFetch('/retreats/' + rtId + '/remind', { method: 'POST', body: JSON.stringify(ids ? { personIds: ids } : {}) });
    const n = res.sent;
    toast(res.suppressed
      ? `${n === 1 ? '1 reminder' : n + ' reminders'} logged (preview mode, not sent)`
      : `${n === 1 ? 'Reminder sent' : n + ' reminders sent'}`);
    await reload();
  } catch (e) { toastError(e.message); }
}

async function showEmail(linkId) {
  try {
    const m = await apiFetch('/retreats/' + rtId + '/links/' + linkId + '/email');
    openModal(`<h3>What they'd get next</h3>
      <div class="rt-sub" style="margin-bottom:4px">To: ${esc(m.to)}</div>
      <div style="font-size:14px;margin-bottom:14px">${esc(m.subject)}</div>
      <div id="rt-email-body" style="border-radius:10px;overflow:hidden"></div>`);
    const box = document.querySelector('.modal-box');
    if (box) box.style.maxWidth = '640px';
    // Built server-side with every name escaped; rendered here exactly as the
    // family's mail client would show it.
    document.getElementById('rt-email-body').innerHTML = m.html;
  } catch (e) { toastError(e.message); }
}

// Firma's download links expire quickly, so one is fetched per click. The tab
// is opened first, synchronously, so the popup blocker treats it as the
// click's own window.
async function openPdf(id) {
  const tab = window.open('', '_blank');
  try {
    const res = await apiFetch('/retreats/' + rtId + '/people/' + id + '/pdf');
    if (tab) tab.location = res.url; else location.href = res.url;
  } catch (e) { if (tab) tab.close(); toastError(e.message); }
}

async function removePerson(id) {
  const p = rtData.people.find(x => x.id === id);
  if (!p) return;
  const note = p.status === 'complete' ? ' They already signed; their signed copy is unaffected.' : ' Any forms they started are cancelled.';
  if (!confirm(`Remove ${p.name} from ${rtData.retreat.name}?${note}`)) return;
  try {
    await apiFetch('/retreats/' + rtId + '/people/' + id, { method: 'DELETE' });
    toast(p.name + ' removed');
    await reload();
  } catch (e) { toastError(e.message); }
}

async function openDrawer(kind) {
  rtDrawer = kind;
  rtPicked = new Set();
  rtFilter = '';
  rtEligible = null;
  redraw();
  if (kind === 'performers') {
    try { rtEligible = await apiFetch('/retreats/' + rtId + '/eligible'); } catch (e) { toastError(e.message); rtDrawer = null; }
    redraw();
  } else {
    const first = document.getElementById('rt-c-first');
    if (first) first.focus();
  }
}

async function addPerformers() {
  const ids = [...rtPicked];
  if (!ids.length) return;
  try {
    const res = await apiFetch('/retreats/' + rtId + '/performers', { method: 'POST', body: JSON.stringify({ performerIds: ids }) });
    toast(res.added === 1 ? '1 performer added' : res.added + ' performers added');
    rtDrawer = null;
    await reload();
  } catch (e) { toastError(e.message); }
}

async function addChaperone(again) {
  const v = id => (document.getElementById(id).value || '').trim();
  const body = { firstName: v('rt-c-first'), lastName: v('rt-c-last'), email: v('rt-c-email'), parentAccountId: Number(v('rt-c-parent')) || null };
  try {
    await apiFetch('/retreats/' + rtId + '/chaperones', { method: 'POST', body: JSON.stringify(body) });
    toast(body.firstName + ' ' + body.lastName + ' added');
    rtDrawer = again ? 'chaperone' : null;
    await reload();
    if (again) document.getElementById('rt-c-first')?.focus();
  } catch (e) { toastError(e.message); }
}
